/**
 * Admin API — global admin endpoints for platform-wide management.
 */

import { z } from "zod";
import { api, apiFetch, apiVoid } from "./client";
import {
  type Address,
  type AdminUser,
  AdminUserSchema,
  type Agreement,
  AgreementSchema,
  AuditFilterOptionsSchema,
  type AuditFilterOptions,
  AuditLogResponseSchema,
  type AuditLogResponse,
  type CompanyDetail,
  CompanyDetailSchema,
  type Company,
  CompanySchema,
  type CompanyNote,
  CompanyNoteSchema,
  type DashboardTemplate,
  DashboardTemplateSchema,
  type GlobalAdminInvitation,
  GlobalAdminInvitationSchema,
  type Invitation,
  InvitationSchema,
  type OnboardingStatus,
  OnboardingStatusSchema,
  type PlatformDashboard,
  PlatformDashboardSchema,
  type User,
  UserSchema,
  PaginatedSchema,
  type PaginatedResponse,
} from "./schemas";

// ── Request types ────────────────────────────────────────

interface CreateCompanyRequest {
  name: string;
  /** Modules to grant at creation time. Empty list = company exists but
   *  cannot be used until a module is granted later. */
  modules?: string[] | undefined;
  marketing_licenses?: number | undefined;
  dashboard_template_ids?: string[] | undefined;
}

interface UpdateCompanyRequest {
  name?: string | undefined;
  /** Full replacement set of module slugs. Use the dedicated grant/revoke
   *  endpoints for incremental edits with their own audit log. */
  modules?: string[] | undefined;
  marketing_licenses?: number | undefined;
  legal_name?: string | undefined;
  country?: string | undefined;
  timezone?: string | undefined;
  vat_id?: string | undefined;
  billing_email?: string | undefined;
  company_address?: Address | undefined;
  billing_address?: Address | undefined;
  /** Jurisdiction identity — values flow into every legal-acceptance
   *  row's snapshot fields. Free-form per country at pilot stage;
   *  per-country whitelisting is post-pilot. */
  legal_form?: string | undefined;
  registry_name?: string | undefined;
  registry_id?: string | undefined;
}

interface InviteGlobalAdminRequest {
  email: string;
}

interface UpdateAdminRequest {
  status?: string | undefined;
  is_global_admin?: boolean | undefined;
}

// ── Company management ───────────────────────────────────

export async function listCompanies(): Promise<CompanyDetail[]> {
  return api("/admin/companies", z.array(CompanyDetailSchema));
}

export async function getCompany(companyId: string): Promise<CompanyDetail> {
  return api(`/admin/companies/${companyId}`, CompanyDetailSchema);
}

export interface ListCompanyUsersParams {
  skip?: number;
  limit?: number;
  search?: string;
}

export async function listCompanyUsers(
  companyId: string,
  params: ListCompanyUsersParams = {},
): Promise<PaginatedResponse<User>> {
  const searchParams = new URLSearchParams();
  if (params.skip !== undefined) searchParams.set("skip", String(params.skip));
  if (params.limit !== undefined) searchParams.set("limit", String(params.limit));
  if (params.search) searchParams.set("search", params.search);
  const qs = searchParams.toString();
  return api(
    `/admin/companies/${companyId}/users${qs ? `?${qs}` : ""}`,
    PaginatedSchema(UserSchema),
  );
}

export async function createCompany(
  data: CreateCompanyRequest,
): Promise<Company> {
  return api("/admin/companies", CompanySchema, {
    method: "POST",
    body: data,
  });
}

export async function updateCompany(
  companyId: string,
  data: UpdateCompanyRequest,
): Promise<Company> {
  return api(`/admin/companies/${companyId}`, CompanySchema, {
    method: "PATCH",
    body: data,
  });
}

// ── Logo upload / delete (GA-scope, any company) ────────────

export async function adminUploadCompanyLogo(
  companyId: string,
  file: File,
): Promise<Company> {
  const form = new FormData();
  form.append("file", file);
  return api(`/admin/companies/${companyId}/logo`, CompanySchema, {
    method: "POST",
    body: form,
  });
}

export async function adminDeleteCompanyLogo(
  companyId: string,
): Promise<Company> {
  return api(`/admin/companies/${companyId}/logo`, CompanySchema, {
    method: "DELETE",
  });
}

export async function setCompanyStatus(
  companyId: string,
  status: string,
  password: string,
  reason: string,
): Promise<void> {
  return apiVoid(`/admin/companies/${companyId}/status`, {
    method: "PATCH",
    body: { status, password, reason },
  });
}

export async function deactivateCompany(
  companyId: string,
  password: string,
  reason: string,
): Promise<void> {
  return apiVoid(`/admin/companies/${companyId}/deactivate`, {
    method: "PATCH",
    body: { password, reason },
  });
}

export async function reactivateCompany(
  companyId: string,
  password: string,
  reason: string,
): Promise<void> {
  return apiVoid(`/admin/companies/${companyId}/reactivate`, {
    method: "PATCH",
    body: { password, reason },
  });
}

export async function activateCompany(
  companyId: string,
  options: {
    status?: "active" | "trial";
    trialEndsAt?: string;
    reason: string;
  },
): Promise<void> {
  const body: Record<string, unknown> = {
    status: options.status ?? "active",
    reason: options.reason,
  };
  if (options.trialEndsAt) {
    body.trial_ends_at = options.trialEndsAt;
  }
  return apiVoid(`/admin/companies/${companyId}/activate`, {
    method: "POST",
    body,
  });
}

export async function inviteCompanyAdmin(
  companyId: string,
  email: string,
): Promise<void> {
  return apiVoid(`/admin/companies/${companyId}/invite-admin`, {
    method: "POST",
    body: { email },
  });
}

/** Record that the offline-signed master contract is on file. Until
 *  this is set, the company's CA cannot complete click-through ToS /
 *  DPA acceptance — the agreement modal stays locked behind an
 *  "awaiting master contract" overlay. ``signedAt`` defaults to now()
 *  on the backend when omitted; supply it explicitly to backdate. */
export async function recordMasterContract(
  companyId: string,
  options: {
    signedBy: string;
    signedAt?: string;
    /** Lowercase-hex sha256 of the offline PDF (64 chars). Optional but
     * recommended — gives auditors a tamper-evidence anchor without
     * the platform ever seeing the PDF. */
    pdfSha256?: string;
  },
): Promise<void> {
  // Deliberately no PDF / URL / file reference. The signed contract
  // contains commercial terms (pricing, payment, custom clauses) that
  // must never be surfaced through the platform — see backend's
  // RecordMasterContractRequest docstring. The platform stores only
  // the audit anchor (signatory name + signing date + sha256
  // fingerprint of the offline PDF, all leak-safe values).
  const body: Record<string, unknown> = {
    signed_by: options.signedBy,
  };
  if (options.signedAt) body.signed_at = options.signedAt;
  if (options.pdfSha256) body.pdf_sha256 = options.pdfSha256;
  return apiVoid(`/admin/companies/${companyId}/master-contract`, {
    method: "PATCH",
    body,
  });
}

/** Clear the master-contract record. Existing acceptances stay
 *  valid; the gate only re-closes for *future* version bumps until
 *  a new record is filed. */
export async function revokeMasterContract(
  companyId: string,
  reason: string,
): Promise<void> {
  return apiVoid(`/admin/companies/${companyId}/master-contract`, {
    method: "DELETE",
    body: { reason },
  });
}

export async function listCompanyInvitations(companyId: string): Promise<Invitation[]> {
  return api(`/admin/companies/${companyId}/invitations`, z.array(InvitationSchema));
}

export async function resendCompanyInvitation(
  companyId: string,
  invitationId: string,
): Promise<void> {
  return apiVoid(`/admin/companies/${companyId}/invitations/${invitationId}/resend`, {
    method: "POST",
  });
}

export async function revokeCompanyInvitation(
  companyId: string,
  invitationId: string,
): Promise<void> {
  return apiVoid(`/admin/companies/${companyId}/invitations/${invitationId}/revoke`, {
    method: "POST",
  });
}

// ── Admin user management ────────────────────────────────

export async function listAdminUsers(): Promise<AdminUser[]> {
  return api("/admin/users", z.array(AdminUserSchema));
}

export async function inviteGlobalAdmin(
  data: InviteGlobalAdminRequest,
): Promise<{ message: string }> {
  return api(
    "/admin/invitations",
    z.object({ message: z.string() }),
    { method: "POST", body: data },
  );
}

export async function listGlobalAdminInvitations(
  status: "pending" | "past" = "pending",
): Promise<GlobalAdminInvitation[]> {
  return api(
    `/admin/invitations?status_filter=${status}`,
    z.array(GlobalAdminInvitationSchema),
  );
}

export async function resendGlobalAdminInvitation(
  invitationId: string,
): Promise<void> {
  return apiVoid(`/admin/invitations/${invitationId}/resend`, {
    method: "POST",
  });
}

export async function revokeGlobalAdminInvitation(
  invitationId: string,
): Promise<void> {
  return apiVoid(`/admin/invitations/${invitationId}/revoke`, {
    method: "POST",
  });
}

export async function updateAdminUser(
  userId: string,
  data: UpdateAdminRequest,
): Promise<AdminUser> {
  return api(`/admin/users/${userId}`, AdminUserSchema, {
    method: "PATCH",
    body: data,
  });
}

// ── Admin security actions ───────────────────────────────

export async function forcePasswordReset(userId: string): Promise<void> {
  return apiVoid(`/admin/users/${userId}/force-password-reset`, {
    method: "POST",
  });
}

export async function killUserSessions(userId: string): Promise<void> {
  return apiVoid(`/admin/users/${userId}/sessions`, {
    method: "DELETE",
  });
}

export async function adminReactivateUser(
  companyId: string,
  userId: string,
  reason: string,
): Promise<void> {
  return apiVoid(
    `/admin/companies/${companyId}/users/${userId}/reactivate`,
    { method: "POST", body: { reason } },
  );
}

// ── Recovery actions (last-resort GA endpoints) ──────────
// All three require password re-auth + reason for SOC2 audit trail.
// Used from the support runbook when a customer locks themselves out.

export async function adminResetUserMfa(
  userId: string,
  password: string,
  reason: string,
): Promise<void> {
  return apiVoid(`/admin/users/${userId}/reset-mfa`, {
    method: "POST",
    body: { password, reason },
  });
}

export async function adminUnlockAccount(
  userId: string,
  password: string,
  reason: string,
): Promise<void> {
  return apiVoid(`/admin/users/${userId}/unlock-account`, {
    method: "POST",
    body: { password, reason },
  });
}

export async function adminDisableCompanyIpAllowlist(
  companyId: string,
  password: string,
  reason: string,
): Promise<void> {
  return apiVoid(`/admin/companies/${companyId}/disable-ip-allowlist`, {
    method: "POST",
    body: { password, reason },
  });
}

// ── Audit logs (global) ──────────────────────────────────

export interface AuditLogParams {
  limit?: number | undefined;
  offset?: number | undefined;
  company_id?: string | undefined;
  action?: string | undefined;
  actor_type?: string | undefined;
  action_category?: string | undefined;
  resource_type?: string | undefined;
  user_id?: string | undefined;
  since?: string | undefined;
  until?: string | undefined;
  search?: string | undefined;
}

export async function listAdminAuditLogs(
  params: AuditLogParams = {},
): Promise<AuditLogResponse> {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.set(key, String(value));
    }
  }
  const qs = searchParams.toString();
  return api(
    `/admin/audit-logs${qs ? `?${qs}` : ""}`,
    AuditLogResponseSchema,
  );
}

export async function getAuditFilterOptions(): Promise<AuditFilterOptions> {
  return api("/admin/audit-logs/filters", AuditFilterOptionsSchema);
}

export async function exportAdminAuditLogsCsv(
  params: AuditLogParams = {},
): Promise<void> {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.set(key, String(value));
    }
  }
  const qs = searchParams.toString();
  const response = await apiFetch(
    `/admin/audit-logs/export${qs ? `?${qs}` : ""}`,
  );
  if (!response.ok) {
    throw new Error("Failed to export audit logs");
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = response.headers.get("content-disposition")?.match(/filename="(.+)"/)?.[1] ?? "audit_logs.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ── Agreements & Onboarding ──────────────────────────

export async function listCompanyAgreements(
  companyId: string,
): Promise<Agreement[]> {
  return api(`/admin/companies/${companyId}/agreements`, z.array(AgreementSchema));
}

export async function getOnboardingStatus(
  companyId: string,
): Promise<OnboardingStatus> {
  return api(`/admin/companies/${companyId}/onboarding-status`, OnboardingStatusSchema);
}

// ── Platform Dashboard ───────────────────────────────

export async function getPlatformDashboard(): Promise<PlatformDashboard> {
  return api("/admin/dashboard", PlatformDashboardSchema);
}

// ── Company Notes ────────────────────────────────────

export async function listCompanyNotes(
  companyId: string,
): Promise<CompanyNote[]> {
  return api(`/admin/companies/${companyId}/notes`, z.array(CompanyNoteSchema));
}

export async function createCompanyNote(
  companyId: string,
  content: string,
): Promise<CompanyNote> {
  return api(`/admin/companies/${companyId}/notes`, CompanyNoteSchema, {
    method: "POST",
    body: { content },
  });
}

export async function deleteCompanyNote(
  companyId: string,
  noteId: string,
): Promise<void> {
  return apiVoid(`/admin/companies/${companyId}/notes/${noteId}`, {
    method: "DELETE",
  });
}

// ── Platform sessions ─────────────────────────────────

export interface AdminSession {
  session_id: string;
  user_id: string;
  user_email: string;
  user_full_name: string;
  company_id: string | null;
  company_name: string | null;
  device_name: string;
  ip_address: string;
  geo_country: string | null;
  geo_city: string | null;
  created_at: string;
  last_activity_at: string | null;
}

const AdminSessionListSchema = z.object({
  items: z.array(z.object({
    session_id: z.string(),
    user_id: z.string(),
    user_email: z.string(),
    user_full_name: z.string(),
    company_id: z.string().nullable(),
    company_name: z.string().nullable(),
    device_name: z.string(),
    ip_address: z.string(),
    geo_country: z.string().nullable(),
    geo_city: z.string().nullable(),
    created_at: z.string(),
    last_activity_at: z.string().nullable(),
  })),
  total: z.number(),
});

export async function listPlatformSessions(
  params: { company_id?: string; skip?: number; limit?: number } = {},
): Promise<{ items: AdminSession[]; total: number }> {
  const query = new URLSearchParams();
  if (params.company_id) query.set("company_id", params.company_id);
  if (params.skip !== undefined) query.set("skip", String(params.skip));
  if (params.limit !== undefined) query.set("limit", String(params.limit));
  const qs = query.toString();
  return api(`/admin/sessions${qs ? `?${qs}` : ""}`, AdminSessionListSchema);
}

export async function killPlatformSession(sessionId: string): Promise<void> {
  return apiVoid(`/admin/sessions/${sessionId}`, { method: "DELETE" });
}

// ── Dashboard Templates ─────────────────────────────────

interface CreateDashboardTemplateRequest {
  name: string;
  display_name?: string | undefined;
  description?: string | undefined;
  widgets?: Record<string, unknown>[] | undefined;
  category?: string | undefined;
  is_active?: boolean | undefined;
}

interface UpdateDashboardTemplateRequest {
  name?: string | undefined;
  display_name?: string | undefined;
  description?: string | undefined;
  widgets?: Record<string, unknown>[] | undefined;
  category?: string | undefined;
  is_active?: boolean | undefined;
}

export async function listDashboardTemplates(
  activeOnly = false,
): Promise<DashboardTemplate[]> {
  const qs = activeOnly ? "?active_only=true" : "";
  return api(
    `/admin/dashboard-templates${qs}`,
    z.array(DashboardTemplateSchema),
  );
}

export async function getDashboardTemplate(
  templateId: string,
): Promise<DashboardTemplate> {
  return api(
    `/admin/dashboard-templates/${templateId}`,
    DashboardTemplateSchema,
  );
}

export async function createDashboardTemplate(
  data: CreateDashboardTemplateRequest,
): Promise<DashboardTemplate> {
  return api("/admin/dashboard-templates", DashboardTemplateSchema, {
    method: "POST",
    body: data,
  });
}

export async function updateDashboardTemplate(
  templateId: string,
  data: UpdateDashboardTemplateRequest,
): Promise<DashboardTemplate> {
  return api(
    `/admin/dashboard-templates/${templateId}`,
    DashboardTemplateSchema,
    { method: "PATCH", body: data },
  );
}

export async function deleteDashboardTemplate(
  templateId: string,
): Promise<void> {
  return apiVoid(`/admin/dashboard-templates/${templateId}`, {
    method: "DELETE",
  });
}

export async function instantiateTemplate(
  templateId: string,
  companyId: string,
): Promise<{ dashboard_id: string; message: string }> {
  return api(
    `/admin/dashboard-templates/${templateId}/instantiate/${companyId}`,
    z.object({ dashboard_id: z.string(), message: z.string() }),
    { method: "POST" },
  );
}
